import { getScreenName, getValidToken } from '@axa-asia/ga-tracker';
import { EmmaInitPayload } from '@axa-asia/hk-emma-js';
import { maskPN } from '@axa-asia/hk-emma-web-module/lib/ga-lib/MaskingUtil';
import { isEmmaApp } from '../helpers/common';

const GA_BANNER_CATEGORY_OTHER = 'Others';
export const transformBannerCategory = (
  bannerCategory = '',
  customBannerCategory = '',
): string => {
  return bannerCategory === GA_BANNER_CATEGORY_OTHER
    ? customBannerCategory
    : bannerCategory;
};

export const handleGaError = (e: any) => {
  const tcWindow: any = window;
  if (!isEmmaApp && !tcWindow.tC) {
    console.warn('GA script is not loaded but event is triggered.');
  } else if ((!isEmmaApp && tcWindow.tC) || isEmmaApp) {
    console.error('GA event error', e);
  }
};

export const trackBannerAction = (
  product,
  banner_action,
  banner_plan_name,
  banner_title,
  bare_screen_path,
  screenNameMapping,
  campaign_name = '',
  banner_category = '',
) => {
  if (isEmmaApp) {
    const screen_path = maskPN(bare_screen_path);
    getScreenName(screen_path, screenNameMapping).then((screen_name) => {
      import('@axa-asia/hk-emma-js').then((mod) => {
        mod.EmmaFactory.createInstance().then((e) => {
          e.instance.trackEvent('click_banner', {
            product,
            banner_action,
            banner_plan_name,
            banner_title,
            screen_path,
            screen_name,
            campaign_name,
            banner_category,
          });
        });
      });
    });
  }
};

export const trackBannerOnDisplayWeb = async (
  product: string | string[],
  planName: string | string[],
  bannerTitle: string,
  bannerAction?: string,
  campaignName = '',
  bannerCategory = '',
  screenNameMapping?,
  emmaJS?,
) => {
  try {
    const tcWindow: any = window;
    const screenPath = window.location.pathname;

    const screenName = await getScreenName(screenPath, screenNameMapping);

    const axaId = getValidToken(emmaJS?.auth?.token);

    // Web GA4
    tcWindow.dataLayer.push({
      event: 'clickBanner',
      product: product,
      bannerAction: bannerAction ? bannerAction : 'click',
      bannerPlanName: planName,
      bannerTitle: bannerTitle,
      screenName: screenName,
      userId: axaId,
      campaignName,
      bannerCategory,
    });

    // APP GA4
    trackBannerAction(
      product,
      bannerAction ? bannerAction : 'click',
      planName,
      bannerTitle,
      screenName,
      screenNameMapping,
      campaignName,
      bannerCategory,
    );
  } catch (e) {
    handleGaError(e);
  }
};

export const gaBannerClick = (
  gaLob: string[],
  title: string,
  bannerAction: string,
  campaignName: string,
  bannerCategory: string,
  customBannerCategory: string,
  screenNameMapping?,
  emmaJS?,
) => {
  try {
    const transformedBannerCategory = transformBannerCategory(
      bannerCategory,
      customBannerCategory,
    );

    trackBannerOnDisplayWeb(
      gaLob,
      gaLob,
      title,
      bannerAction,
      campaignName,
      transformedBannerCategory,
      screenNameMapping,
      emmaJS,
    );
  } catch (e) {
    console.error(e);
  }
};

export const handleEmmaDeeplink = (
  fullLink: string,
  emmaJS?: EmmaInitPayload,
) => {
  const parsedUrl = new URL(fullLink);
  let deeplink = parsedUrl.searchParams.get('url');
  if (deeplink?.startsWith('tel:')) {
    deeplink = deeplink.replace(' ', '+'); // Fix for searchParams.get will replace '+' to ' '
  }
  if (!isEmmaApp) {
    if (deeplink?.startsWith('https')) {
      window.open(deeplink, '_blank');
      return;
    }
    if (deeplink?.startsWith('emma-webview')) {
      // check the index of "?"
      const index = deeplink.indexOf('?');
      if (index !== -1) {
        deeplink = deeplink.substring(index + 1);
      }
      const urlSearchParam = new URLSearchParams(deeplink);
      const url = urlSearchParam?.get('url');
      if (url) {
        window.open(url, '_blank');
      }
      return;
    }
  }
  if (deeplink) {
    emmaJS?.instance?.goTo(deeplink);
  }
  console.warn(`deeplink is empty for ${fullLink}`);
  return;
};

const AXA_DOMAIN_REGEX =
  /^https:\/\/(?:www|uat-www|preprod-www)\.[\w-]*axa\.com\.hk.*/g;

export const isCorpApp = (link: string) => {
  return AXA_DOMAIN_REGEX.test(link);
};

export const addParamsForCorpWeb = (link: string) => {
  const newUrl = new URL(link);
  if (!newUrl.searchParams.get('platform')) {
    newUrl.searchParams.append('platform', 'emmaportal');
  }
  return newUrl.toString();
};

export const handleExternalLink = (link: string, inNewTab: boolean) => {
  const newLink = isCorpApp(link) ? addParamsForCorpWeb(link) : link;

  if (inNewTab) {
    window.open(newLink, '_blank');
  } else {
    window.location.href = newLink;
  }
};

export const formatXsellBanner = (
  data,
  lang,
  emmaJS,
  closedXsellBannerFromCustState,
  closedXsellBannerFromLocalState,
  limit?: number,
  screenNameMapping?,
  gaLob?: string[],
) => {
  const result = data
    .filter((item) => {
      // // For close mode 1 & 3
      const isClosedBeforeInCustState = !!closedXsellBannerFromCustState?.find(
        (sbObj) => sbObj && sbObj.eventId === item.data.event_id,
      );

      // // For close mode 2 & 4
      const isClosedBeforeInLocalState =
        !!closedXsellBannerFromLocalState?.find(
          (sbObj) => sbObj && sbObj.eventId === item.data.event_id,
        );

      if (isClosedBeforeInLocalState || isClosedBeforeInCustState) {
        return false;
      }

      return true;
    })
    ?.map((item) => {
      const linkPayload = isEmmaApp
        ? item.data?.destination_url_APP
        : item.data?.destination_url_WEB;

      const eventId = item.data.event_id || '';
      const destinationLink =
        linkPayload?.link + `?xsellBanner_eventId=${eventId}`;

      const closeMode =
        ['Forever', 'Login Session', '72 hours', 'Page'].indexOf(
          item.data.display_mode,
        ) + 1;

      const gaCampaignName = item.data?.ga_campaign_name || '';
      const gaBannerCategory = item.data?.ga_banner_category || '';
      const gaCustomBannerCategory = item.data?.ga_custom_banner_category || '';

      const pageId = item.data.page_id || '';
      const displayName = item.data.display_name || '';
      const gaBannerTitle = `XS - ${pageId} - ${displayName}`;

      return {
        eventId: eventId,
        iconKey:
          item.data.header_icon1 === 'none' ? '' : item.data.header_icon1,
        timerDescription: item.data.header_title || '',
        startDateTime: item.data.header_start_date,
        endDateTime: item.data.header_end_date,
        imageSrc: item.data.banner_image?.src,
        bannerHeader: item.data.banner_title || '',
        bannerDescription: item.data.banner_text || '',
        textLink: item.data.text_link_label || '',
        isTooltip: item.data.animated_dialog_display,
        isCountdownHeader: item.data.countdown_header_display,
        tooltipContent: item.data.dialog_tagline || '',
        isTimerBarCardVisible: true,
        onClick: () => {
          gaBannerClick(
            gaLob,
            gaBannerTitle,
            'click',
            gaCampaignName,
            gaBannerCategory,
            gaCustomBannerCategory,
            screenNameMapping,
            emmaJS,
          );
          if (destinationLink && destinationLink.match(/^(https:\/\/)/)) {
            if (destinationLink.match(/^(https:\/\/emma-deeplink)/)) {
              handleEmmaDeeplink(destinationLink, emmaJS);
              return;
            } else {
              handleExternalLink(destinationLink, linkPayload.inNewTab);
              return;
            }
          } else if (linkPayload.document && destinationLink) {
            let deeplink: string;
            if (isEmmaApp) {
              deeplink = `cms-webview?url=${destinationLink}&isHideBottomTabs=true`; // always not show bottom bar, aligned with xsell in app
            } else {
              deeplink = `emma-cms?path=${destinationLink}`;
            }
            emmaJS?.instance?.goTo(deeplink);
            return;
          }

          return;
        },
        closeMode: closeMode?.toString() || '',
        closeButtonDisplay: item.data.close_button_display,
        gaCampaignName,
        gaBannerCategory,
        gaCustomBannerCategory,
        pageId: pageId,
        displayName: displayName,
      };
    })
    ?.sort((a, b) => {
      const sequenceA = a.sequence || 0;
      const sequenceB = b.sequence || 0;
      return sequenceA - sequenceB;
    });

  return limit ? result?.slice(0, limit) : result;
};
